import React, { useContext, useEffect, useReducer } from 'react'
import ZutoAnalyticsHelper from '@zuto-internal/zuto-analytics-helper'
import $console from '@zuto/utilities/src/$console'
import { AddressHistoryContext, AddressDetails, AddressHistoryEntry } from '../AddressHistoryContext'
import { AddressDetailsQuestion } from '../AddressDetails'
import AddressSituation from '../AddressSituation'
import TimeInAddressSituation from '../TimeInAddressSituation'
import AddressHistoryEntryContext, { AddressHistoryEntryState, AddressSubQuestion } from './AddressHistoryEntryContext'
import AddressHistoryEntryReducer from './AddressHistoryEntryReducer'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import { nameNumberFieldBuilder } from '../../../../utils/utils'
import { HistoryEntryProps } from '../../HistoryEntry/HistoryEntryProps'
import { fireReactGAEvent } from '../../utils'
import { PrefillReviewPageApplicationFormContext } from '../../../ApplicationForm/PrefillReviewPageApplicationFormContext'

const AddressEntry = ({
  existingEntry,
  disablePrevious = false,
  entryId,
  startAtEnd = false,
  updateFollowingPrefillStep
} : HistoryEntryProps<AddressHistoryEntry>) : JSX.Element => {
  const { setQuestionName } = useContext(PrefillReviewPageApplicationFormContext)
  const { dispatch: addressHistoryDispatch } = useContext(AddressHistoryContext)
  const { featureSwitches, loggingSwitches } = useContext(ApplicationFormContext)
  const { reorderQuestions, reviewPageV3 } = featureSwitches ?? {}
  const { logManualAddressEntryFields } = loggingSwitches ?? {}
  const zutoAnalyticsHelper = new ZutoAnalyticsHelper()

  const addressDetailsComplete = (addressDetails?: AddressDetails) => !!addressDetails
    && !!(addressDetails.flatNumber || addressDetails.buildingName || addressDetails.buildingNumber)
    && !!addressDetails.street
    && !!addressDetails.town
    && !!addressDetails.postcode

  const calculateStartingQuestion = () : AddressSubQuestion => {
    if (reviewPageV3) return 'ADDRESS_DETAILS'
    if (existingEntry === undefined) return 'ADDRESS_DETAILS'
    if (startAtEnd) {
      return reorderQuestions
        ? 'TIME_IN_SITUATION'
        : 'STATUS'
    }
    if (existingEntry.entrySource === 'FORM' || !addressDetailsComplete(existingEntry.addressDetails)) {
      return 'ADDRESS_DETAILS'
    }
    if (reorderQuestions) {
      if (!existingEntry.lengthOfTime) return 'TIME_IN_SITUATION'
    } else if (!existingEntry.lengthOfTime) return 'TIME_IN_SITUATION'
    return 'ADDRESS_DETAILS'
  }

  const initialState : AddressHistoryEntryState = {
    entry: existingEntry ?? { entrySource: 'FORM' },
    question: calculateStartingQuestion()
  }

  const [state, dispatch] = useReducer(AddressHistoryEntryReducer(reorderQuestions === true), initialState)

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('addressHistory')
    }
  }, [])

  useEffect(() => {
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label: `qst.addressHistory.${parseInt(entryId, 10) - 1}`,
      nonInteraction: true
    })
  }, [])

  useEffect(() => {
    switch (state.escapeAction) {
      case 'NEXT': {
        addressHistoryDispatch({
          type: 'UPDATE_ENTRY',
          index: entryId,
          value: state.entry
        })
        addressHistoryDispatch({
          type: 'NEXT'
        })
        if (logManualAddressEntryFields) {
          try {
            zutoAnalyticsHelper.sendCustomEvent({
              eventType: 'addressManualEntry',
              eventCategory: 'Address Entry',
              addressFields: state.entry?.addressDetails,
              // eslint-disable-next-line max-len
              nameNumberField: nameNumberFieldBuilder(state.entry?.addressDetails?.flatNumber, state.entry?.addressDetails?.buildingName, state.entry?.addressDetails?.buildingNumber),
              cardlessAddressJourney: true
            })
          } catch (e) {
            $console.error('zutoAnalyticsHelper.sendCustomEvent error', e)
          }
        }

        break
      }
      case 'PREVIOUS': {
        addressHistoryDispatch({
          type: 'UPDATE_ENTRY',
          index: entryId,
          value: state.entry
        })
        addressHistoryDispatch({
          type: 'PREVIOUS'
        })
        break
      }
      default:
        break
    }
  }, [state.escapeAction])

  const buildEntryTitle = (addressDetails? : AddressDetails) : string | undefined => {
    if (addressDetails === undefined) return undefined
    const addressTitleParts : string[] = []
    if (addressDetails.flatNumber || addressDetails.buildingName) {
      addressTitleParts.push([addressDetails.flatNumber, addressDetails.buildingName].filter(Boolean).join(' '))
    }
    if (addressDetails.buildingNumber || addressDetails.street) {
      addressTitleParts.push([addressDetails.buildingNumber, addressDetails.street].filter(Boolean).join(' '))
    }
    if (addressDetails.postcode) {
      addressTitleParts.push(addressDetails.postcode)
    }
    return addressTitleParts.join(', ')
  }

  const renderQuestion = () : JSX.Element => {
    switch (state.question) {
      case 'ADDRESS_DETAILS':
        return (
          <AddressDetailsQuestion
            disablePrevious={disablePrevious}
            entryId={entryId}
            existingDetails={state.entry.addressDetails}
            existingDisplayState={state.entry.displayState ? state.entry.displayState : 'SEARCH_RESULTS'}
            existingLoqateResults={state.entry.loqateResults}
            existingPostcodeSearchLabel={state.entry.addressLabel}
            existingPostcodeSearchAttempted={state.entry.postcodeSearchAttempted}
            updateFollowingPrefillStep={updateFollowingPrefillStep}
          />
        )
      case 'STATUS':
        return (
          <AddressSituation
            disablePrevious={disablePrevious}
            entryId={entryId}
            existingSituation={state.entry.addressSituation}
          />
        )
      case 'TIME_IN_SITUATION':
        return (
          <TimeInAddressSituation
            disablePrevious={disablePrevious}
            entryId={entryId}
            existingDuration={state.entry.lengthOfTime}
            entryTitle={buildEntryTitle(state.entry.addressDetails)}
            updateFollowingPrefillStep={updateFollowingPrefillStep}
          />
        )
      default:
        return <></>
    }
  }

  return (
    <AddressHistoryEntryContext.Provider value={{ dispatch }}>
      {renderQuestion()}
    </AddressHistoryEntryContext.Provider>
  )
}

export default AddressEntry
